"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Coins, SlidersHorizontal as Sliders, Eye, Award, Target, ArrowRight, CircleCheck as CheckCircle2, CircleX as XCircle, Sparkles, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const keyBenefits = [{
  id: 1,
  title: "Direct Expert Access",
  iconName: "UserCheck",
  highlight: "100% Direct Contact",
  description: "You work directly with me, Yassine Essaadi. I personally build and execute your strategy—no account managers or junior delegates standing between us.",
  consultantAdvantage: "Direct collaboration with senior specialist",
  agencyAlternative: "Delegated to junior staff & account handlers"
}, {
  id: 2,
  title: "Cost Efficiency & Max ROI",
  iconName: "Coins",
  highlight: "Zero Agency Overhead",
  description: "Every pound of your retainer pays directly for senior SEO expertise and technical execution, eliminating costly agency overheads and fluff.",
  consultantAdvantage: "100% budget spent on impactful SEO execution",
  agencyAlternative: "30-50% lost to account management & overheads"
}, {
  id: 3,
  title: "Agile & Bespoke Strategy",
  iconName: "Sliders",
  highlight: "Tailored to Your Revenue",
  description: "I construct custom organic search roadmaps built exclusively around your conversion goals and pivot rapidly as search engines evolve.",
  consultantAdvantage: "Fast implementation and custom roadmaps",
  agencyAlternative: "Rigid cookie-cutter templates & slow approvals"
}, {
  id: 4,
  title: "Radical Transparency",
  iconName: "Eye",
  highlight: "Honest Metrics",
  description: "You receive clear deliverables, real search performance data, and direct updates on what is working—without vanity metrics or jargon.",
  consultantAdvantage: "Clear business metrics & direct accountability",
  agencyAlternative: "Automated standard reports hiding weak results"
}, {
  id: 5,
  title: "Senior Technical Mastery",
  iconName: "Award",
  highlight: "10+ Years Hands-On",
  description: "From complex technical audits to AI Search Optimisation (GEO), you leverage deep, hands-on knowledge earned over a decade in search engine strategy.",
  consultantAdvantage: "Proven senior specialist executing the work",
  agencyAlternative: "Inexperienced staff learning on your project"
}, {
  id: 6,
  title: "Strictly Capped Roster",
  iconName: "Target",
  highlight: "Dedicated Attention",
  description: "I intentionally limit the number of clients I accept at one time. This guarantees your business gets my undivided focus and proactive care.",
  consultantAdvantage: "High-touch partnership & quick communication",
  agencyAlternative: "High client turnover & stretched account teams"
}];
const comparisonMatrix = [{
  id: 1,
  feature: "Primary Point of Contact",
  consultant: "Yassine Essaadi (Lead SEO Consultant)",
  agency: "Account Manager (Non-specialist)"
}, {
  id: 2,
  feature: "Strategy Execution",
  consultant: "Handcrafted & executed directly by me",
  agency: "Outsourced or assigned to junior interns"
}, {
  id: 3,
  feature: "Flexibility & Agility",
  consultant: "Immediate adjustments based on real data",
  agency: "Bureaucratic approval layers and delays"
}, {
  id: 4,
  feature: "Pricing Structure",
  consultant: "Transparent fee reflecting true senior value",
  agency: "Padded retainers funding lavish offices"
}, {
  id: 5,
  feature: "AI Search & GEO Readiness",
  consultant: "Proactive modern AI search integration",
  agency: "Legacy keyword tactics from years past"
}];
export default function ConsultantBenefits() {
  const prefersReducedMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "UserCheck":
        return <UserCheck className="size-5 text-primary" />;
      case "Coins":
        return <Coins className="size-5 text-primary" />;
      case "Sliders":
        return <Sliders className="size-5 text-primary" />;
      case "Eye":
        return <Eye className="size-5 text-primary" />;
      case "Award":
        return <Award className="size-5 text-primary" />;
      case "Target":
        return <Target className="size-5 text-primary" />;
      default:
        return <Sparkles className="size-5 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantbenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border/40">
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <Badge variant="secondary" className="px-3 py-1 bg-secondary text-foreground font-medium rounded-full border border-accent/30 inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-accent" />
            <span>Why Work Directly With Me</span>
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Independent SEO Expertise vs. The Agency Model
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            When you partner with YE Consultancy, you work directly with me—Yassine Essaadi.
            I deliver personal accountability, senior technical execution, and lean cost efficiency
            without the bloat of traditional marketing agencies.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {keyBenefits.map((benefit, i) => <motion.div key={benefit.id} variants={itemVariants} data-index={i}>
              <Card className="h-full bg-card border-border hover:border-accent/40 transition-[border-color,box-shadow] duration-300 shadow-xs flex flex-col justify-between overflow-hidden group">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center border border-accent/20">
                      {getIcon(benefit.iconName)}
                    </div>
                    <Badge className="bg-secondary text-foreground hover:bg-secondary/80 border-accent/30 text-xs px-2.5 py-0.5 rounded-full">
                      {benefit.highlight}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground pt-1">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>

                  <div className="pt-2 border-t border-border/60 space-y-2 text-xs">
                    <div className="flex items-start gap-2 text-foreground font-medium">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>{benefit.consultantAdvantage}</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <XCircle className="size-4 text-muted-foreground/60 shrink-0 mt-0.5" />
                      <span>{benefit.agencyAlternative}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="bg-card rounded-2xl p-6 sm:p-8 lg:p-10 border border-border shadow-xs space-y-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-6">
            <div>
              <span className="text-xs font-semibold tracking-wider uppercase text-accent font-mono">
                Side-by-side comparison
              </span>
              <h3 className="text-2xl font-semibold text-foreground mt-1">
                At a Glance: How We Compare
              </h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-lg border border-border/50 self-start md:self-auto">
              <ShieldCheck className="size-4 text-primary" />
              <span>Direct personal accountability guaranteed</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-border text-muted-foreground font-medium">
                  <th className="py-3 px-4 w-1/3">Key Dimension</th>
                  <th className="py-3 px-4 w-1/3 bg-secondary/50 rounded-t-lg text-foreground font-semibold">
                    YE Consultancy (Yassine)
                  </th>
                  <th className="py-3 px-4 w-1/3">Traditional SEO Agency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {comparisonMatrix.map((row, i) => <tr key={row.id} data-index={i} className="hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4 font-medium text-foreground">
                      {row.feature}
                    </td>
                    <td className="py-4 px-4 bg-secondary/30 text-foreground font-medium">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-accent shrink-0" />
                        <span>{row.consultant}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <XCircle className="size-4 text-muted-foreground/60 shrink-0" />
                        <span>{row.agency}</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-secondary/60 rounded-2xl p-8 sm:p-10 border border-accent/30 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-serif">
              Ready for high-converting organic search growth?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Book a free SEO strategy consultation directly with me. We will discuss your current search visibility, identify technical bottlenecks, and map out a sustainable growth plan tailored to your business.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 font-medium shadow-xs">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border bg-background hover:bg-muted text-foreground rounded-lg px-6 font-medium">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}